import type { Opt } from '@jet/environment';
import type { Localization } from '../../dependencies/localization';
import { inject, type ObjectGraph } from '@jet/environment/dependencies';
import { ObjectGraphType } from '@jet-app/music-classical/dependencies';
import type { Section } from '@jet-app/music-classical/models';

import { getBaseSeoData } from './BaseSeoData';
import type { SeoData } from './types';
import type { ArtistPage } from '@jet-app/music-classical/models/pages/artist';
import {
    createActionSchema,
    ACTION_SCHEMA_TYPE,
    schemaDataForAlbum,
    schemaDataForSubjectOf,
} from '@jet-app/music-classical/utilities/seo/utils';
import { buildSrc } from '@jet-app/music-classical/utilities/seo/utils';
import type { Lockup } from '@jet-app/music-classical/models/section/Lockup';

/**
 * @method getArtistSeoData
 * @param {string} url Artist canonical URL
 * @param {ArtistPage} page artist page object
 * @param {ObjectGraph} objectGraph
 * @returns {SeoData}
 */
export function getArtistSeoData(
    url: string,
    page: ArtistPage,
    objectGraph: ObjectGraph,
): SeoData {
    const localization: Localization = inject(
        ObjectGraphType.localization,
        objectGraph,
    );
    const baseSeoData: SeoData = getBaseSeoData(url, objectGraph);
    const siteName: string = localization.string(
        'ASE.Web.MusicClassicalClientJS.Name',
    );
    const artistName = page.header.title;

    function getPageTitle() {
        if (artistName) {
            return localization.string(
                'ASE.Web.MusicClassicalClientJS.ContentA.Dash.ContentB',
                {
                    contentA: artistName,
                    contentB: siteName,
                },
            );
        }

        return baseSeoData.pageTitle;
    }

    function getPageDescription() {
        if (artistName) {
            return localization.string(
                'ASE.Web.MusicClassicalClientJS.Meta.Artist.Description',
                {
                    artistName,
                },
            );
        }

        return baseSeoData.description;
    }

    const pageTitle: string = getPageTitle();
    const description: Opt<string> = getPageDescription();
    const artworkUrl: Opt<string> = page.header.artwork?.template;
    const albumsSection: Section<Lockup> = page.sections?.find(
        (section) => section.id === 'list_album',
    );
    const wellKnownWorksSection: Section<Lockup> = page.sections?.find(
        (section) => section.id === 'list_work',
    );

    /**
     *
     * Generate SEO data object
     *
     */
    const seoData = {
        ...baseSeoData,
        pageTitle,
        description,

        /**
         * Social OG Tags
         */
        socialTitle: pageTitle,
        socialDescription: description,

        /**
         *
         * Apple-specific Values
         *
         */
        appleContentId: undefined,
        appleTitle: pageTitle,
        appleDescription: description,

        /**
         *
         * Artist Structured Data/schema.org Values
         *
         */
        schemaName: 'schema:music-group',
        schemaContent: {
            '@context': 'http://schema.org',
            '@type': 'MusicGroup',
            album: albumsSection?.items.map((album) =>
                schemaDataForAlbum(album, baseSeoData),
            ),
            description,
            name: artistName,
            potentialAction: createActionSchema(url, ACTION_SCHEMA_TYPE.Listen),
            subjectOf: wellKnownWorksSection?.items.map((work) =>
                schemaDataForSubjectOf(work),
            ),
            url,
            image: artworkUrl
                ? buildSrc(
                      artworkUrl,
                      {
                          width: 486,
                          height: 486,
                          crop: 'bb',
                          fileType: baseSeoData.fileType,
                      },
                      {},
                  )
                : null,
        },
    };

    return seoData;
}

export function addingArtistSeoData(
    url: string,
    page: ArtistPage,
    objectGraph: ObjectGraph,
): ArtistPage {
    return {
        ...page,
        seoData: getArtistSeoData(url, page, objectGraph),
    };
}
